import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { validateCPF, validatePhone } from "@/utils/functions";
import { setCustomer } from "@/redux/ducks/customer";
import { ReducersTypes } from "@/redux/reducers/Models";
import { NavigateFunction } from "react-router-dom";
import { PersonalDataForm } from "../Models";

const BIRTHDATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

const personalDataSchema = z.object({
  personType: z.enum(["fisica", "juridica"]),
  name: z.string().min(2, "Nome obrigatório"),
  document: z.string().min(1, "Documento obrigatório"),
  email: z.string().email("E-mail inválido"),
  phone: z.string().min(1, "Telefone obrigatório"),
  birthdate: z.string(),
  isAcceptedTerms: z.boolean().refine(val => val === true, "Você deve aceitar os Termos de Uso e Política de Privacidade"),
  isForeigner: z.boolean(),
  razaoSocial: z.string(),
}).superRefine((data, ctx) => {
  if (data.personType === "fisica") {
    if (!data.document.match(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/) || !validateCPF(data.document)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "CPF inválido", path: ["document"] });
    }
    if (!data.phone.match(/^\(\d{2}\) \d{5}-\d{4}$/) || !validatePhone(data.phone)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Celular inválido", path: ["phone"] });
    }
  } else {
    if (!data.document.match(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "CNPJ inválido", path: ["document"] });
    }
    if (!data.phone.match(/^\(\d{2}\) \d{5}-\d{4}$/)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Telefone inválido", path: ["phone"] });
    }
    if (!data.razaoSocial || data.razaoSocial.trim().length < 2) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Razão social obrigatória", path: ["razaoSocial"] });
    }
    if (!data.birthdate || !BIRTHDATE_REGEX.test(data.birthdate)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Data de nascimento obrigatória", path: ["birthdate"] });
    }
  }
});

export default function usePersonalDataForm(navigate: NavigateFunction, onSuccess?: () => void) {
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);

  const defaultValues: PersonalDataForm = {
    personType: "fisica",
    name: customer?.register?.name ?? "",
    document: customer?.register?.document ?? "",
    email: customer?.register?.email ?? "",
    phone: customer?.register?.phone ?? "",
    birthdate: customer?.register?.birthdate ?? "",
    isAcceptedTerms: false,
    isForeigner: false,
    razaoSocial: "",
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<PersonalDataForm>({
    resolver: zodResolver(personalDataSchema),
    mode: "onChange",
    defaultValues,
  });

  const switchPersonType = useCallback(
    (type: "fisica" | "juridica") => {
      reset({
        personType: type,
        name: "",
        document: "",
        email: "",
        phone: "",
        birthdate: "",
        isAcceptedTerms: false,
        isForeigner: false,
        razaoSocial: "",
      });
    },
    [reset]
  );

  const watchedValues = watch();
  const { personType, name, document, email, phone, birthdate, isAcceptedTerms, razaoSocial } = watchedValues;

  const onSubmit = useCallback(
    (data: PersonalDataForm) => {
      dispatch(
        setCustomer({
          ...customer,
          register: {
            ...customer?.register,
            ...data,
          }
        })
      );
      onSuccess?.();
      navigate("/register/choosepin");
      reset();
    },
    [dispatch, reset, customer, navigate, onSuccess]
  );

  let isFormValid = false;

  if (personType === "fisica") {
    isFormValid = !!(
      name?.trim().length >= 2 &&
      document?.length === 14 && validateCPF(document) &&
      email?.includes("@") && email.includes(".") &&
      phone?.length === 15 && validatePhone(phone) &&
      isAcceptedTerms === true
    );
  } else {
    isFormValid = !!(
      name?.trim().length >= 2 &&
      document?.length === 18 &&
      email?.includes("@") && email.includes(".") &&
      phone?.length === 15 &&
      birthdate?.length === 10 && BIRTHDATE_REGEX.test(birthdate) &&
      razaoSocial?.trim().length >= 2 &&
      isAcceptedTerms === true
    );
  }

  return {
    isDisabled: !isFormValid,
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    watch,
    setValue,
    switchPersonType,
  };
}
